import React, { useState, useEffect, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import '../styles/LoginSignup.css';

// Loader Component
const Loader = () => (
  <div className="loader-overlay">
    <div className="loader" />
    <style jsx>{`
      .loader-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(250, 247, 237, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
      }
      .loader {
        border: 4px solid #E5E7EB;
        border-top: 4px solid #B45309;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close-button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-content">{children}</div>
      </div>
    </div>
  );
};

function LoginSignup() {
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState('login');
  const [otpSent, setOtpSent] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    loginPassword: false,
    signupPassword: false,
    signupConfirmPassword: false,
  });
  const [formData, setFormData] = useState({
    loginEmail: '',
    loginPassword: '',
    signupName: '',
    signupEmail: '',
    signupAddress: '',
    signupPhone: '',
    signupOtp: '',
    signupPassword: '',
    signupConfirmPassword: '',
    forgotPasswordEmail: '',
  });
  const [errors, setErrors] = useState({});

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const startResendCountdown = () => {
    setResendTimer(30);
  };

  const resetSignupForm = () => {
    setFormData({
      ...formData,
      signupName: '',
      signupEmail: '',
      signupAddress: '',
      signupPhone: '',
      signupOtp: '',
      signupPassword: '',
      signupConfirmPassword: '',
    });
    setOtpSent(false);
    setOtpModalOpen(false);
    setPasswordModalOpen(false);
    setErrors({});
    setResendTimer(0);
  };

  const validateSignupFields = () => {
    const newErrors = {};
    if (!formData.signupName) newErrors.signupName = 'Name is required';
    if (!formData.signupEmail) newErrors.signupEmail = 'Email is required';
    if (!formData.signupAddress) newErrors.signupAddress = 'Address is required';
    if (!formData.signupPhone) newErrors.signupPhone = 'Phone number is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.signupEmail && !emailRegex.test(formData.signupEmail)) {
      newErrors.signupEmail = 'Please enter a valid email';
    }
    if (formData.signupPhone && formData.signupPhone.length < 10) {
      newErrors.signupPhone = 'Phone number must be at least 10 digits';
    }
    return newErrors;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const newErrors = validateSignupFields();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/send-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.signupName,
          email: formData.signupEmail,
          address: formData.signupAddress,
          phone: formData.signupPhone,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setOtpSent(true);
        setOtpModalOpen(true);
        startResendCountdown();
      } else {
        setErrors({ ...errors, signupEmail: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, signupEmail: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/resend-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.signupEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        startResendCountdown();
      } else {
        setErrors({ ...errors, signupOtp: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, signupOtp: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelOtp = async () => {
    setIsLoading(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/auth/cancel-signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.signupEmail }),
      });
    } catch (error) {
      console.error('Error cancelling signup:', error);
    } finally {
      setIsLoading(false);
      resetSignupForm();
      setActiveForm('signup');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!formData.signupOtp) {
      setErrors({ ...errors, signupOtp: 'OTP is required' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.signupEmail,
          otp: formData.signupOtp,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setOtpModalOpen(false);
        setPasswordModalOpen(true);
      } else {
        setErrors({ ...errors, signupOtp: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, signupOtp: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.signupPassword) newErrors.signupPassword = 'Password is required';
    if (formData.signupPassword && formData.signupPassword.length < 6) {
      newErrors.signupPassword = 'Password must be at least 6 characters';
    }
    if (formData.signupPassword !== formData.signupConfirmPassword) {
      newErrors.signupConfirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.signupEmail,
          password: formData.signupPassword,
        }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setPasswordModalOpen(false);
        // Fetch user data after registration
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          credentials: 'include',
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        setActiveForm('login');
        setFormData({
          loginEmail: formData.signupEmail,
          loginPassword: '',
          signupName: '',
          signupEmail: '',
          signupAddress: '',
          signupPhone: '',
          signupOtp: '',
          signupPassword: '',
          signupConfirmPassword: '',
          forgotPasswordEmail: '',
        });
        setOtpSent(false);
        navigate('/');
      } else {
        setErrors({ ...errors, signupPassword: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, signupPassword: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.forgotPasswordEmail) {
      setErrors({ ...errors, forgotPasswordEmail: 'Email is required' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.forgotPasswordEmail)) {
      setErrors({ ...errors, forgotPasswordEmail: 'Please enter a valid email' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.forgotPasswordEmail }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setForgotPasswordModalOpen(false);
        setFormData({ ...formData, forgotPasswordEmail: '' });
        setErrors({ ...errors, forgotPasswordEmail: 'Reset link sent to your email' });
      } else {
        setErrors({ ...errors, forgotPasswordEmail: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, forgotPasswordEmail: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.loginEmail) newErrors.loginEmail = 'Email is required';
    if (!formData.loginPassword) newErrors.loginPassword = 'Password is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.loginEmail,
          password: formData.loginPassword,
        }),
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
          credentials: 'include',
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
        navigate('/');
      } else {
        setErrors({ ...errors, loginEmail: data.message });
      }
    } catch (error) {
      setErrors({ ...errors, loginEmail: 'Server error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  return (
    <main className="login-signup-main-container">
      {isLoading && <Loader />}
      <section className="login-hero-section-container">
        <div className="login-hero-content-container">
          <h1 className="login-hero-title">{activeForm === 'login' ? 'Login' : 'Sign Up'}</h1>
          <p className="login-hero-subtitle">
            {activeForm === 'login'
              ? 'Sign in to explore eco-friendly cycling tours with Beaver Tours.'
              : 'Create an account to start your cycling journey today.'}
          </p>
        </div>
      </section>
      <section className="login-signup-section-container">
        <div className="login-section-container">
          <AnimatePresence mode="wait">
            <motion.div
              className="login-form-container"
              key={activeForm}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={formVariants}
            >
              {activeForm === 'login' && (
                <form className="login-form" onSubmit={handleLoginSubmit}>
                  <div className="form-group">
                    <label htmlFor="loginEmail">Email</label>
                    <input
                      type="email"
                      id="loginEmail"
                      name="loginEmail"
                      value={formData.loginEmail}
                      onChange={handleInputChange}
                      placeholder="Your Email"
                      required
                    />
                    {errors.loginEmail && <span className="error">{errors.loginEmail}</span>}
                    {errors.forgotPasswordEmail && (
                      <span className={errors.forgotPasswordEmail.includes('sent') ? 'success' : 'error'}>
                        {errors.forgotPasswordEmail}
                      </span>
                    )}
                  </div>
                  <div className="form-group password-group">
                    <label htmlFor="loginPassword">Password</label>
                    <div className="password-input-container">
                      <input
                        type={showPassword.loginPassword ? 'text' : 'password'}
                        id="loginPassword"
                        name="loginPassword"
                        value={formData.loginPassword}
                        onChange={handleInputChange}
                        placeholder="Your Password"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => togglePasswordVisibility('loginPassword')}
                      >
                        {showPassword.loginPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {errors.loginPassword && <span className="error">{errors.loginPassword}</span>}
                  </div>
                  <div className="form-links forgot-password-container">
  <button
    type="button"
    className="forgot-password-link"
    onClick={() => setForgotPasswordModalOpen(true)}
    disabled={isLoading}
  >
    Forgot Password?
  </button>
</div>
                  <button type="submit" className="form-submit-button" disabled={isLoading}>
                    Login
                  </button>
                  <div className="form-links">
                    <button
                      type="button"
                      className="signup-link"
                      onClick={() => {
                        setActiveForm('signup');
                        setErrors({});
                        setOtpSent(false);
                      }}
                      disabled={isLoading}
                    >
                      Don't have an account? Sign Up
                    </button>
                  </div>
                </form>
              )}
              {activeForm === 'signup' && (
                <form className="signup-form" onSubmit={handleSendOtp}>
                  <div className="form-group">
                    <label htmlFor="signupName">Full Name</label>
                    <input
                      type="text"
                      id="signupName"
                      name="signupName"
                      value={formData.signupName}
                      onChange={handleInputChange}
                      placeholder="Your Full Name"
                      required
                    />
                    {errors.signupName && <span className="error">{errors.signupName}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="signupEmail">Email</label>
                    <input
                      type="email"
                      id="signupEmail"
                      name="signupEmail"
                      value={formData.signupEmail}
                      onChange={handleInputChange}
                      placeholder="Your Email"
                      required
                    />
                    {errors.signupEmail && <span className="error">{errors.signupEmail}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="signupAddress">Address</label>
                    <input
                      type="text"
                      id="signupAddress"
                      name="signupAddress"
                      value={formData.signupAddress}
                      onChange={handleInputChange}
                      placeholder="Your Address"
                      required
                    />
                    {errors.signupAddress && <span className="error">{errors.signupAddress}</span>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="signupPhone">Phone Number</label>
                    <input
                      type="tel"
                      id="signupPhone"
                      name="signupPhone"
                      value={formData.signupPhone}
                      onChange={handleInputChange}
                      placeholder="Your Phone Number"
                      required
                    />
                    {errors.signupPhone && <span className="error">{errors.signupPhone}</span>}
                  </div>
                  <button type="submit" className="form-submit-button" disabled={otpSent || isLoading}>
                    Send OTP
                  </button>
                  <div className="form-links">
                    <button
                      type="button"
                      className="login-link"
                      onClick={() => {
                        setActiveForm('login');
                        setErrors({});
                        setOtpSent(false);
                      }}
                      disabled={isLoading}
                    >
                      Already have an account? Login
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
      <Modal isOpen={otpModalOpen} onClose={handleCancelOtp} title="Verify OTP">
        <form onSubmit={handleVerifyOtp}>
          <div className="form-group">
            <label htmlFor="signupOtp">Enter OTP</label>
            <input
              type="text"
              id="signupOtp"
              name="signupOtp"
              value={formData.signupOtp}
              onChange={handleInputChange}
              placeholder="Enter 6-digit OTP"
              required
            />
            {errors.signupOtp && <span className="error">{errors.signupOtp}</span>}
          </div>
          <div className="resend-otp-container">
            <span
              className={`resend-otp-text ${resendTimer > 0 || isLoading ? 'disabled' : ''}`}
              onClick={resendTimer === 0 && !isLoading ? handleResendOtp : undefined}
            >
              {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
            </span>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="modal-button cancel-button"
              onClick={handleCancelOtp}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-button" disabled={isLoading}>
              Verify
            </button>
          </div>
        </form>
      </Modal>
      <Modal isOpen={passwordModalOpen} onClose={() => setPasswordModalOpen(false)} title="Set Password">
        <form onSubmit={handleSetPassword}>
          <div className="form-group password-group">
            <label htmlFor="signupPassword">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword.signupPassword ? 'text' : 'password'}
                id="signupPassword"
                name="signupPassword"
                value={formData.signupPassword}
                onChange={handleInputChange}
                placeholder="Your Password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('signupPassword')}
              >
                {showPassword.signupPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.signupPassword && <span className="error">{errors.signupPassword}</span>}
          </div>
          <div className="form-group password-group">
            <label htmlFor="signupConfirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                type={showPassword.signupConfirmPassword ? 'text' : 'password'}
                id="signupConfirmPassword"
                name="signupConfirmPassword"
                value={formData.signupConfirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm Password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('signupConfirmPassword')}
              >
                {showPassword.signupConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.signupConfirmPassword && (
              <span className="error">{errors.signupConfirmPassword}</span>
            )}
          </div>
          <div className="modal-footer">
            <button type="submit" className="modal-button" disabled={isLoading}>
              Complete Registration
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={forgotPasswordModalOpen}
        onClose={() => setForgotPasswordModalOpen(false)}
        title="Forgot Password"
      >
        <form onSubmit={handleForgotPassword}>
          <div className="form-group">
            <label htmlFor="forgotPasswordEmail">Email</label>
            <input
              type="email"
              id="forgotPasswordEmail"
              name="forgotPasswordEmail"
              value={formData.forgotPasswordEmail}
              onChange={handleInputChange}
              placeholder="Your Email"
              required
            />
            {errors.forgotPasswordEmail && (
              <span className={errors.forgotPasswordEmail.includes('sent') ? 'success' : 'error'}>
                {errors.forgotPasswordEmail}
              </span>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="modal-button cancel-button"
              onClick={() => setForgotPasswordModalOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className="modal-button" disabled={isLoading}>
              Send Reset Link
            </button>
          </div>
        </form>
      </Modal>
      <style jsx>{`
        .password-group {
          position: relative;
        }
        .password-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        .password-input-container input {
          width: 100%;
          padding-right: 40px; /* Space for the eye button */
        }
        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 16px;
          padding: 0;
          outline: none;
        }
        .forgot-password-link {
          font-size: 14px;
          color: #B45309;
          text-decoration: underline;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-bottom: 10px;
          display: inline-block;
        }
        .forgot-password-link:disabled {
          color: #6B4E31;
          cursor: not-allowed;
        }
        .success {
          color: #2E7D32;
          font-size: 12px;
          margin-top: 5px;
          display: block;
        }
      `}</style>
    </main>
  );
}

export default LoginSignup;